import React from "react";
import { Listbox } from "@headlessui/react";

// Types for different data structures
export interface DropdownOption {
  id: string | number;
  name: string;
  [key: string]: any; // Allow additional properties
}

export interface ListboxDropdownProps {
  // Core functionality
  options: DropdownOption[];
  value?: DropdownOption | DropdownOption[] | null;
  onChange: (value: DropdownOption | DropdownOption[] | null) => void;
  multiple?: boolean;

  // Display options
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;

  // Styling
  className?: string;
  buttonClassName?: string;
  optionsClassName?: string;
  width?: string;
  height?: string;

  // Validation
  error?: string | { message?: string };
  isSubmitted?: boolean;

  // Advanced options
  displayProperty?: string; // Property to display (default: 'name')
  valueProperty?: string; // Property to use as value (default: 'id')
  searchable?: boolean;
  clearable?: boolean;
  maxDisplayItems?: number; // Max items to show in button text before "X items selected"

  // Nullable/clearable options
  includeSelectOption?: boolean; // Include a "Select..." option that clears the selection
  selectOptionText?: string; // Text for the select option (default: "Select...")

  // Custom rendering
  renderOption?: (
    option: DropdownOption,
    selected: boolean
  ) => React.ReactElement;
  renderButton?: (
    selectedOptions: DropdownOption[],
    placeholder: string,
    loading: boolean
  ) => React.ReactNode;

  // Loading state
  loadingText?: string;
  emptyText?: string;

  // Z-index for dropdowns
  zIndex?: string;

  // Alternative rendering modes
}

const ListboxDropdown: React.FC<ListboxDropdownProps> = ({
  options = [],
  value,
  onChange,
  multiple = false,
  label,
  placeholder = "Select option",
  required = false,
  disabled = false,
  loading = false,
  className = "",
  buttonClassName = "",
  optionsClassName = "",
  width = "w-full",
  height = "h-11",
  error,
  isSubmitted = false,
  displayProperty = "name",
  valueProperty = "id",
  // searchable = false,
  // clearable = false,
  maxDisplayItems = 2,
  includeSelectOption = false,
  selectOptionText = "Select...",
  renderOption,
  renderButton,
  loadingText = "Loading...",
  emptyText = "No options available",
  zIndex = "z-[1000]",
}) => {
  // Normalize value to always be an array for easier handling
  const selectedOptions = React.useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Handle selection change
  const handleSelectionChange = (
    newValue: DropdownOption | DropdownOption[]
  ) => {
    if (multiple) {
      onChange(Array.isArray(newValue) ? newValue : [newValue]);
    } else {
      onChange(Array.isArray(newValue) ? newValue[0] || null : newValue);
    }
  };

  // Handle individual option toggle for multiple selection
  // const handleOptionToggle = (option: DropdownOption) => {
  //   if (!multiple) {
  //     onChange(option);
  //     return;
  //   }

  //   const isSelected = selectedOptions.some(
  //     (selected) => +selected[valueProperty] === +option[valueProperty]
  //   );

  //   let newSelection: DropdownOption[];
  //   if (isSelected) {
  //     newSelection = selectedOptions.filter(
  //       (selected) => +selected[valueProperty] !== +option[valueProperty]
  //     );
  //   } else {
  //     newSelection = [...selectedOptions, option];
  //   }

  //   onChange(newSelection.length > 0 ? newSelection : null);
  // };

  // Get display text for the button
  const getDisplayText = () => {
    if (loading) return loadingText;
    if (selectedOptions.length === 0) return placeholder;

    if (multiple) {
      if (selectedOptions.length <= maxDisplayItems) {
        return selectedOptions
          .map((option) => option[displayProperty])
          .join(", ");
      } else {
        return `${selectedOptions.length} items selected`;
      }
    } else {
      return selectedOptions[0]?.[displayProperty] || placeholder;
    }
  };

  // Check if an option is selected
  const isOptionSelected = (option: DropdownOption) => {
    return selectedOptions.some(
      (selected) => +selected[valueProperty] === +option[valueProperty]
    );
  };

  // Render error message
  const renderError = () => {
    if (!isSubmitted || !error) return null;

    const errorMessage = typeof error === "string" ? error : error.message;
    if (!errorMessage) return null;

    return (
      <p className="text-red-500 text-xs mt-1 min-h-[16px]">{errorMessage}</p>
    );
  };

  // Default button renderer
  const defaultButtonRenderer = (
    selectedOpts: DropdownOption[],
    isLoading: boolean
  ) => (
    <Listbox.Button
      disabled={disabled || isLoading}
      className={`${width} ${height} px-3.5 py-2.5 bg-white border border-[#D5D7DA] rounded-lg shadow-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between ${
        isLoading
          ? "text-gray-400"
          : selectedOpts.length > 0
            ? "text-black"
            : "text-[#717680]"
      } ${
        disabled || isLoading
          ? "opacity-50 cursor-not-allowed"
          : "cursor-pointer"
      } ${buttonClassName}`}
    >
      <span className="truncate flex-1 mr-2" title={getDisplayText()}>
        {getDisplayText()}
      </span>
      {isLoading ? (
        <svg
          className="w-4 h-4 animate-spin flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        <svg
          className="w-4 h-4 transition-transform flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      )}
    </Listbox.Button>
  );

  // Default option renderer
  const defaultOptionRenderer = (option: DropdownOption, selected: boolean) => (
    <div className="flex items-center justify-between">
      <span
        className={`text-sm truncate ${
          selected ? "text-blue-900" : "text-gray-700"
        }`}
      >
        {option[displayProperty]}
      </span>
      {selected && (
        <svg
          className="w-4 h-4 text-blue-600 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </div>
  );

  return (
    <div className={`${className}`}>
      {/* Label */}
      {label && (
        <label className="block mb-2 text-sm font-medium text-gray-700">
          {label} {required && <span className="text-blue-600">*</span>}
        </label>
      )}

      <Listbox
        value={multiple ? selectedOptions : selectedOptions[0] || null}
        onChange={multiple ? onChange : handleSelectionChange}
        multiple={multiple}
        disabled={disabled || loading}
      >
        <div className="relative">
          {/* Button */}
          {renderButton
            ? renderButton(selectedOptions, placeholder, loading)
            : defaultButtonRenderer(selectedOptions, loading)}

          {/* Options */}
          <Listbox.Options
            className={`absolute ${zIndex} mt-1 ${width} bg-white border border-[#D5D7DA] rounded-lg shadow-lg max-h-48 overflow-y-auto focus:outline-none ${optionsClassName}`}
          >
            {loading ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {loadingText}
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">{emptyText}</div>
            ) : (
              <>
                {/* Include select option if specified and not in multiple mode */}
                {includeSelectOption && !multiple && (
                  <Listbox.Option
                    key="select-option"
                    value={null}
                    className={({ selected }) =>
                      `relative cursor-pointer select-none py-2 px-3 ${
                        selected ? "bg-blue-50 text-blue-900" : "text-gray-500"
                      }`
                    }
                  >
                    {({ selected }) => (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500 truncate">
                          {selectOptionText}
                        </span>
                        {selected && (
                          <svg
                            className="w-4 h-4 text-blue-600 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    )}
                  </Listbox.Option>
                )}

                {/* Regular options */}
                {options.map((option) => (
                  <Listbox.Option
                    key={option[valueProperty]}
                    value={option}
                    className={({ selected }) =>
                      `relative cursor-pointer select-none py-2 px-3 ${
                        selected ? "bg-blue-50 text-blue-900" : "text-gray-900"
                      } ${
                        multiple && isOptionSelected(option)
                          ? "bg-blue-50 text-blue-900"
                          : ""
                      }`
                    }
                  >
                    {({ selected }) => {
                      const isSelected = multiple
                        ? isOptionSelected(option)
                        : selected;
                      return renderOption
                        ? renderOption(option, isSelected)
                        : defaultOptionRenderer(option, isSelected);
                    }}
                  </Listbox.Option>
                ))}
              </>
            )}
          </Listbox.Options>
        </div>
      </Listbox>

      {/* Error message */}
      {renderError()}
    </div>
  );
};

export default ListboxDropdown;
